import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Container,
    Typography,
    Button,
    Box,
    Card,
    CardContent,
} from '@mui/material';
import CalendarHeatmap from 'react-calendar-heatmap';
import 'react-calendar-heatmap/dist/styles.css';

type HeatmapValue = {
    date: string;
    count: number;
};

type ReturnState = {
    returnPath?: string;
};

const DashboardPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const state = location.state as ReturnState;
    const returnPath = state?.returnPath ?? '/';

    const [values, setValues] = useState<HeatmapValue[]>([]);
    const [numViewers, setNumViewers] = useState('');
    const [numEditors, setNumEditors] = useState('');
    const [numRecords, setNumRecords] = useState('');

    const endDate = new Date();
    endDate.setDate(endDate.getDate() - 1);
    //console.log(endDate);

    // Calculate the date 364 days ago
    const daysBack = 364;
    const date364DaysAgo = new Date();
    date364DaysAgo.setDate(date364DaysAgo.getDate() - daysBack - 1);

    // Find the previous (or same) Monday before that date
    const dayOfWeek = date364DaysAgo.getDay() + 1;
    const startDate = new Date(date364DaysAgo);
    startDate.setDate(startDate.getDate() - dayOfWeek);
    //console.log(startDate);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await fetch('/api/records/counts-by-date');
                const data = await res.json();

                setNumViewers(
                    await (await fetch('/api/users/viewers/count')).text()
                );
                setNumEditors(
                    await (await fetch('/api/users/editors/count')).text()
                );
                setNumRecords(await (await fetch('/api/records/count')).text());

                //console.log('Heatmap API response:', data);

                const res_array = data.date_counts;
                // Ensure it's an array and well-formed
                if (Array.isArray(res_array)) {
                    setValues(res_array);
                } else {
                    console.error('Expected an array, got:', res_array);
                    setValues([]);
                }
            } catch {
                console.error('Failed to load heatmap data or other stats');
                setValues([]);
            }
        };
        fetchData();
    }, []);

    return (
        <Container maxWidth="md" sx={{ py: 6 }}>
            <Button
                variant="outlined"
                sx={{ mb: 2 }}
                onClick={() => navigate(returnPath)}
            >
                ← Back
            </Button>

            <Box>
                <Typography
                    variant="h4"
                    gutterBottom
                    sx={{
                        bgcolor: 'grey.400',
                        px: 2,
                        py: 1,
                        fontWeight: 'bold',
                    }}
                >
                    Dashboard Page
                </Typography>
            </Box>

            <Card
                elevation={4}
                sx={{
                    borderRadius: 3,
                    p: 3,
                    backgroundColor: 'white',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                }}
            >
                <CardContent>
                    <Typography
                        variant="h6"
                        gutterBottom
                        sx={{ fontWeight: 600, mb: 2 }}
                    >
                        Log History
                    </Typography>

                    <CalendarHeatmap
                        startDate={new Date(startDate)}
                        endDate={new Date(endDate)}
                        values={values}
                        showWeekdayLabels={true}
                        classForValue={(value) => {
                            if (!value || value.count === undefined)
                                return 'color-empty';
                            if (value.count < 2) return 'color-github-1';
                            if (value.count < 5) return 'color-github-2';
                            if (value.count < 10) return 'color-github-3';
                            return 'color-github-4';
                        }}
                        tooltipDataAttrs={(value) => {
                            if (!value || value.count === undefined)
                                return { 'data-tip': 'No data' };
                            return {
                                'data-tip': `${value.date}: ${value.count} records`,
                            };
                        }}
                    />

                    {/* Stats Row */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'baseline',
                            gap: 5,
                            flexWrap: 'wrap',
                            fontSize: '1rem',
                            fontWeight: 500,
                            color: 'text.secondary',
                        }}
                    >
                        <Typography variant="body1">
                            Total Records: <strong>{numRecords}</strong>
                        </Typography>
                        <Typography variant="body1">
                            Viewers: <strong>{numViewers}</strong>
                        </Typography>
                        <Typography variant="body1">
                            Editors: <strong>{numEditors}</strong>
                        </Typography>
                    </Box>
                </CardContent>
            </Card>
        </Container>
    );
};

export default DashboardPage;
